import { type Ref, ref, watch } from 'vue'
import { useLoadInventFilterDataV3 } from './api.ts'
import type { InventoryFieldFilter, ObjectTypeFilterState } from './types.ts'

export const useModel = (objectId: Ref<number>) => {
  const filters = ref<ObjectTypeFilterState[]>([])
  const selectedIds = ref<number[]>([])
  const fieldFilters = ref<InventoryFieldFilter[]>([])

  const queryResult = useLoadInventFilterDataV3(objectId)

  const isSubtypeFilter = (item: ObjectTypeFilterState) => item.options[0]?.type === 'subtype'

  /**
   * Синхронизирует выбранные типы и значения полей с фильтром карты.
   */
  const syncFilters = () => {
    const ids = filters.value.flatMap((item) => {
      if (isSubtypeFilter(item)) return item.selectedOptionIds

      return item.checked ? [item.inventory_type_id] : []
    })

    selectedIds.value = ids.length ? [...new Set(ids)] : [-1]
    fieldFilters.value = filters.value.flatMap((item) => {
      if (
        isSubtypeFilter(item) ||
        !item.field ||
        !item.checked ||
        item.selectedOptionIds.length === item.options.length
      ) {
        return []
      }

      const selectedIds = new Set(item.selectedOptionIds)
      // Псевдо-размещение идентифицируется props_id, обычное поле — field_id.
      const fieldRef =
        item.field.props_id != null ? { props_id: item.field.props_id } : { field_id: item.field.id }

      return [
        {
          inventory_type_id: item.inventory_type_id,
          ...fieldRef,
          values: item.options
            .filter((option) => selectedIds.has(option.id))
            .map((option) => option.id),
        },
      ]
    })
  }

  watch(
    queryResult.data,
    (data) => {
      if (!Array.isArray(data)) return

      filters.value = data.map((item) => {
        return {
          ...item,
          checked: true,
          selectedOptionIds: item.options.map((option) => option.id),
        }
      })
      syncFilters()
    },
    { immediate: true },
  )

  /**
   * Переключает отправляемый в фильтр тип объекта.
   */
  const toggle = (objectTypeId: number) => {
    const item = filters.value.find((filter) => filter.object_type.id === objectTypeId)
    if (!item) return

    if (item.options.length) {
      item.checked = item.selectedOptionIds.length !== item.options.length
      item.selectedOptionIds = item.checked ? item.options.map((option) => option.id) : []
    } else {
      item.checked = !item.checked
    }

    syncFilters()
  }

  /**
   * Обновляет выбранные варианты группы.
   */
  const updateOptions = (objectTypeId: number, optionIds: number[]) => {
    const item = filters.value.find((filter) => filter.object_type.id === objectTypeId)
    if (!item) return

    item.selectedOptionIds = optionIds
    item.checked = optionIds.length > 0
    syncFilters()
  }

  /**
   * Переключает вариант внутри раскрытой группы.
   */
  const toggleOption = (objectTypeId: number, optionId: number) => {
    const item = filters.value.find((filter) => filter.object_type.id === objectTypeId)
    if (!item) return

    const optionIds = item.selectedOptionIds.includes(optionId)
      ? item.selectedOptionIds.filter((id) => id !== optionId)
      : [...item.selectedOptionIds, optionId]

    updateOptions(objectTypeId, optionIds)
  }

  const getCheckedState = (item: ObjectTypeFilterState) => {
    if (!item.options.length) return item.checked

    return item.selectedOptionIds.length === item.options.length
  }

  const isIndeterminate = (item: ObjectTypeFilterState) =>
    item.options.length > 0 &&
    item.selectedOptionIds.length > 0 &&
    item.selectedOptionIds.length < item.options.length

  const selectAll = () => {
    filters.value.forEach((item) => {
      item.checked = true
      item.selectedOptionIds = item.options.map((option) => option.id)
    })
    syncFilters()
  }

  const deselectAll = () => {
    filters.value.forEach((item) => {
      item.checked = false
      item.selectedOptionIds = []
    })
    syncFilters()
  }

  return {
    filters,
    selectedIds,
    fieldFilters,
    isFetching: queryResult.isFetching,
    isError: queryResult.isError,

    toggle,
    toggleOption,
    getCheckedState,
    isIndeterminate,
    selectAll,
    deselectAll,
  }
}
